import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface NoticiaFeed {
  id: number;
  titulo: string;
  resumo: string | null;
  conteudo: string | null;
  fonte: string | null;
  url: string | null;
  fonte_url: string | null;
  data_publicacao: string | null;
  created_at: string;
}

export interface FonteNoticia {
  id: number;
  nome: string;
  url: string;
  tipo: 'rss' | 'html' | 'api';
  rss_url: string | null;
  api_url: string | null;
  palavras_chave: string[] | null;
  ativo: boolean;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

interface NoticiasFeedResponse {
  success: boolean;
  data: {
    noticias: NoticiaFeed[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

interface FontesResponse {
  success: boolean;
  data: {
    fontes: FonteNoticia[];
  };
}

interface CreateFontePayload {
  nome: string;
  url: string;
  tipo: 'rss' | 'html' | 'api';
  rss_url?: string;
  api_url?: string;
  palavras_chave?: string[];
}

interface CreateFonteResponse {
  success: boolean;
  data: {
    fonte: FonteNoticia;
  };
}

export function useNoticiasFeed(params?: { search?: string; source?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['noticias-feed', params],
    queryFn: async () => {
      const response = await api.get<NoticiasFeedResponse>('/noticias/feed', {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.source ? { source: params.source } : {})
        }
      });

      return response.data;
    }
  });
}

export interface NoticiaRelacionada {
  id: number;
  titulo: string;
  resumo: string | null;
  fonte: string | null;
  url: string | null;
  dataPublicacao: string | null;
  score: number;
  motivo: string;
}

export interface NoticiasRelacionadasResponse {
  configurado: boolean;
  scoreMinimo: number | null;
  noticias: NoticiaRelacionada[];
}

export function useNoticiasRelacionadas(empresaId: number | undefined) {
  return useQuery({
    queryKey: ['noticias-relacionadas', empresaId],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: NoticiasRelacionadasResponse }>(
        `/empresas/${empresaId}/noticias-relacionadas`,
        { params: { limit: 5 } }
      );
      return response.data;
    },
    enabled: typeof empresaId === 'number' && !Number.isNaN(empresaId)
  });
}

export function useNoticiasFontes() {
  return useQuery({
    queryKey: ['noticias-fontes'],
    queryFn: async () => {
      const response = await api.get<FontesResponse>('/noticias/fontes');
      return response.data.fontes;
    }
  });
}

export function useCreateNoticiaFonte() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateFontePayload) => {
      const response = await api.post<CreateFonteResponse>('/noticias/fontes', payload);
      return response.data.fonte;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['noticias-fontes'] });
    }
  });
}
